import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Modal,
  TextField,
  CircularProgress,
  Card,
  Chip,
  Grid,
  IconButton,
  Divider,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionIcon from "@mui/icons-material/Description";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCommentIcon from "@mui/icons-material/AddComment";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import api from "../../../utils/api";

const ArchitecturalRequestVisits = ({ architectural_request_id, currentUserId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const softCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    backgroundColor: "#fff",
  };

  const softInsetSx = {
    borderRadius: "14px",
    border: "1px solid rgba(47,99,173,0.08)",
    backgroundColor: "rgba(47,99,173,0.03)",
  };

  const sectionIconWrapSx = {
    width: 36,
    height: 36,
    minWidth: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(47,99,173,0.08)",
    flexShrink: 0,
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await api.get(`/getEvents`);
        const filteredEvents = response.data.filter(
          (event) => event.architectural_request_id === Number(architectural_request_id)
        );
        setEvents(filteredEvents);
      } catch (error) {
        console.error("Error fetching events:", error);
      } finally {
        setLoading(false);
      }
    };

    if (architectural_request_id) {
      fetchEvents();
    }
  }, [architectural_request_id]);

  const fetchComments = async (eventId) => {
    setIsCommentsLoading(true);
    try {
      const response = await api.get(
        `/getArchitecturalComments/${architectural_request_id}/${eventId}`
      );
      setComments(response.data || []);
    } catch (error) {
      console.error("Failed to fetch comments:", error);
    } finally {
      setIsCommentsLoading(false);
    }
  };

  const handleOpenComments = (event) => {
    setSelectedEventId(event.id);
    setSelectedEvent(event);
    setIsModalOpen(true);
    fetchComments(event.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      alert("Comment cannot be empty.");
      return;
    }

    try {
      await api.post(`/addArchitecturalComment`, {
        architectural_request_id,
        event_id: selectedEventId,
        user_id: currentUserId,
        comment_text: newComment,
        is_internal: false,
      });

      setComments((prevComments) => [
        {
          comment_text: newComment,
          sent_at: new Date().toISOString(),
          user_name: "You",
          user_lastname: "",
        },
        ...prevComments,
      ]);

      setNewComment("");
    } catch (error) {
      console.error("Failed to add comment:", error);
      alert("Failed to add comment. Please try again.");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <EventNoteIcon sx={{ color: "#2f63ad" }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Visits
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Scheduled visits related to this architectural request.
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {events.length === 0 ? (
            <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                No visits found
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                Visits related to this architectural request will appear here.
              </Typography>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {events.map((event) => (
                <Grid item xs={12} key={event.id}>
                  <Card sx={{ ...softCardSx, p: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        gap: 1.5,
                        flexWrap: "wrap",
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, color: "#223142", mb: 0.25 }}
                        >
                          {event.title || `Visit #${event.id}`}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b778c", fontWeight: 600 }}
                        >
                          Event ID: {event.id}
                        </Typography>
                      </Box>

                      <Chip
                        size="small"
                        label={`Request #${event.architectural_request_id}`}
                        sx={{
                          borderRadius: "999px",
                          fontWeight: 700,
                          color: "#2f63ad",
                          backgroundColor: "rgba(47,99,173,0.10)",
                        }}
                      />
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card sx={{ ...softInsetSx, p: 1.5, height: "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={sectionIconWrapSx}>
                              <DescriptionIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                              >
                                Description
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#223142",
                                  fontWeight: 600,
                                  wordBreak: "break-word",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {event.description || "No description"}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ ...softInsetSx, p: 1.5, height: "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={sectionIconWrapSx}>
                              <AccessTimeIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                              >
                                Start Time
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                                {new Date(event.start_time).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ ...softInsetSx, p: 1.5, height: "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={sectionIconWrapSx}>
                              <AccessTimeIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                              >
                                End Time
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                                {new Date(event.end_time).toLocaleString()}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card sx={{ ...softInsetSx, p: 1.5, height: "100%" }}>
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                            <Box sx={sectionIconWrapSx}>
                              <AssignmentIndIcon sx={{ color: "#2f63ad", fontSize: 18 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: "#8a94a6", display: "block", mb: 0.3 }}
                              >
                                Created By
                              </Typography>
                              <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                                {event.created_by_user_id || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 1.5, display: "flex", justifyContent: "flex-end" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleOpenComments(event)}
                        sx={secondaryButtonSx}
                      >
                        View/Add Comments
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Card>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 680 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
                Visit Comments
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                Request ID: {architectural_request_id}
                {selectedEventId ? ` • Event ID: ${selectedEventId}` : ""}
              </Typography>
            </Box>

            <IconButton onClick={() => setIsModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3 }}>
            {selectedEvent && (
              <Card sx={{ ...softInsetSx, p: 2, mb: 2, backgroundColor: "#fff" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
                  {selectedEvent.title || `Visit #${selectedEvent.id}`}
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>
                  {selectedEvent.description || "No description"}
                </Typography>
              </Card>
            )}

            {isCommentsLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress />
              </Box>
            ) : comments.length > 0 ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
                {comments.map((comment, index) => (
                  <Card
                    key={index}
                    sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
                      backgroundColor: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid rgba(47,99,173,0.08)",
                        backgroundColor: "rgba(47,99,173,0.05)",
                      }}
                    >
                      <Chip
                        label="Visit Comment"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "999px",
                          color: "#1d4ed8",
                          backgroundColor: "rgba(59,130,246,0.12)",
                        }}
                      />
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#223142",
                          whiteSpace: "pre-wrap",
                          mb: 1.25,
                        }}
                      >
                        {comment.comment_text}
                      </Typography>

                      <Divider sx={{ my: 1.5, borderColor: "rgba(47,99,173,0.08)" }} />

                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b778c",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <AccessTimeIcon sx={{ fontSize: 14 }} />
                        Added by {comment.user_name} {comment.user_lastname || ""} on{" "}
                        {new Date(comment.sent_at).toLocaleString()}
                      </Typography>
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : (
              <Card sx={{ ...pageCardSx, p: 3, textAlign: "center", mb: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
                  No comments available
                </Typography>
                <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
                  Add the first comment for this visit.
                </Typography>
              </Card>
            )}

            <TextField
              fullWidth
              label="Add a Comment"
              multiline
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button
                variant="outlined"
                onClick={() => setIsModalOpen(false)}
                sx={secondaryButtonSx}
              >
                Close
              </Button>

              <Button
                variant="contained"
                startIcon={<AddCommentIcon />}
                onClick={handleAddComment}
                sx={primaryButtonSx}
              >
                Save Comment
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ArchitecturalRequestVisits;